import type {CourseThemeDocument} from 'course/models/courseThemeDocument';

/**
 * The primary button color a theme gets when neither the public theme doc nor
 * the course sets one. Bootstrap's own `.btn-primary` is left untouched while
 * the theme's value equals this default, so untouched courses keep Bootstrap
 * blue buttons.
 */
export const DEFAULT_BUTTON_COLOR = '#2a61d1';

/**
 * A single color mapping: a color field of {@link CourseThemeDocument} paired
 * with the CSS custom property or variable it should populate.
 */
type ColorVarMapping = readonly [keyof CourseThemeDocument, string];

/**
 * Maps color fields of {@link CourseThemeDocument} to the Bootstrap CSS
 * variables they override (Bootstrap 5.3+ reads these at render time). Setting
 * them retints Bootstrap's own components — body, links, and cards — so a
 * course's color theme reaches Bootstrap markup (e.g. the application's
 * `Card`/`.card` surfaces) without refactoring each one. Only a course that
 * opts into these fields is affected; untouched courses emit nothing.
 */
export const BOOTSTRAP_VAR_MAP: readonly ColorVarMapping[] = [
  ['bodyBackgroundColor', '--bs-body-bg'],
  ['bodyTextColor', '--bs-body-color'],
  ['linkColor', '--bs-link-color'],
  ['linkColor', '--bs-link-hover-color'],
  // Bridge surfaces to Bootstrap cards so `Card`/`.card` follow the theme.
  ['surfaceColor', '--bs-card-bg'],
  ['surfaceTextColor', '--bs-card-color'],
];

/**
 * Bootstrap 5.3 paints several things from `r, g, b` triplet variables rather
 * than the color variables above — anchors read `--bs-link-color-rgb`, and the
 * `.text-body` / `.bg-body` utilities read the body triplets — so those need
 * populating too. Only hex colors can be converted; a theme that uses another
 * notation simply skips these.
 */
export const BOOTSTRAP_RGB_VAR_MAP: readonly ColorVarMapping[] = [
  ['bodyBackgroundColor', '--bs-body-bg-rgb'],
  ['bodyTextColor', '--bs-body-color-rgb'],
  ['linkColor', '--bs-link-color-rgb'],
  ['linkColor', '--bs-link-hover-color-rgb'],
];

/**
 * Maps color fields of {@link CourseThemeDocument} to the generic CSS custom
 * properties the app exposes for theming. Components (styled-components,
 * inline styles, SCSS) read these via `var(--course-…, <fallback>)`, so a
 * single theme value can retint markup across every styling system without
 * coupling to Bootstrap. A field left unset in the theme emits no variable, so
 * each consumer's fallback (its pre-theme value) applies unchanged.
 */
export const COURSE_COLOR_VAR_MAP: readonly ColorVarMapping[] = [
  ['bodyBackgroundColor', '--course-bg'],
  ['bodyTextColor', '--course-fg'],
  ['surfaceColor', '--course-surface'],
  ['surfaceTextColor', '--course-surface-fg'],
  ['linkColor', '--course-link'],
  ['accentColor', '--course-accent'],
];

/**
 * Tints of `--course-accent` for selected and hovered states on themed chrome.
 * Each entry is a CSS custom property name paired with the percentage of the
 * accent mixed into the surface color, so the tint is opaque and reads
 * correctly on light and dark surfaces alike. Emitted only when `accentColor`
 * is set.
 */
export const ACCENT_TINT_VARS: readonly (readonly [string, number])[] = [
  ['--course-accent-soft', 14],
  ['--course-accent-faint', 7],
];

/**
 * Converts a hex color to the `r, g, b` triplet Bootstrap's `-rgb` variables
 * expect.
 *
 * @param color - A CSS color string. Only `#rgb` and `#rrggbb` forms convert.
 * @return The triplet (for example `"140, 21, 21"`), or `null` when `color` is
 *     not a 3- or 6-digit hex color.
 * @throws Never.
 */
export function hexToRgbTriplet(color: string): string | null {
  const match = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(color.trim());
  if (!match) {
    return null;
  }
  const hex = match[1];
  const full =
    hex.length === 3
      ? hex
          .split('')
          .map(c => c + c)
          .join('')
      : hex;
  const r = parseInt(full.slice(0, 2), 16);
  const g = parseInt(full.slice(2, 4), 16);
  const b = parseInt(full.slice(4, 6), 16);
  return `${r}, ${g}, ${b}`;
}

/**
 * Builds the CSS text that applies a course's color theme: a `:root` block
 * declaring the generic `--course-*` custom properties (including the accent
 * tints in {@link ACCENT_TINT_VARS} and, when both body colors are set, the
 * derived `--course-muted-fg` and `--course-border`), the Bootstrap bridge
 * variables and their `-rgb` triplets, a `body` rule so the page background
 * and text color follow the theme, `.btn-primary` / `.btn-outline-primary`
 * overrides when `buttonColorPrimary` differs from {@link DEFAULT_BUTTON_COLOR},
 * and an `.alert-info` override tinted from the accent when `accentColor` is
 * set.
 * Only fields present (non-empty) on `theme` emit rules, so an untouched
 * course produces an empty string and renders exactly as before.
 *
 * @param theme - The resolved course theme (color fields are read; other
 *     fields are ignored). Fields that are absent or empty strings are skipped.
 * @return A CSS string suitable for injection into a `<style>` element. Returns
 *     an empty string when no color fields are set.
 * @throws Never.
 */
export function buildThemeColorStyles(
  theme: Partial<CourseThemeDocument>,
): string {
  const rootDeclarations: string[] = [];

  for (const [field, cssVar] of [...COURSE_COLOR_VAR_MAP, ...BOOTSTRAP_VAR_MAP]) {
    const value = theme[field];
    if (typeof value === 'string' && value) {
      rootDeclarations.push(`${cssVar}: ${value};`);
    }
  }

  for (const [field, cssVar] of BOOTSTRAP_RGB_VAR_MAP) {
    const value = theme[field];
    const triplet = typeof value === 'string' && value ? hexToRgbTriplet(value) : null;
    if (triplet) {
      rootDeclarations.push(`${cssVar}: ${triplet};`);
    }
  }

  if (theme.accentColor) {
    for (const [cssVar, percent] of ACCENT_TINT_VARS) {
      rootDeclarations.push(
        `${cssVar}: color-mix(in srgb, var(--course-accent) ${percent}%, var(--course-surface, white));`,
      );
    }
  }

  // Muted text and hairline borders derived from the body pair, so secondary
  // headings and card edges sit between foreground and background on any theme.
  if (theme.bodyTextColor && theme.bodyBackgroundColor) {
    rootDeclarations.push(
      '--course-muted-fg: color-mix(in srgb, var(--course-fg) 65%, var(--course-bg));',
      '--course-border: color-mix(in srgb, var(--course-fg) 15%, var(--course-bg));',
    );
  }

  const primary = theme.buttonColorPrimary;
  const hasCustomPrimary = Boolean(primary) && primary !== DEFAULT_BUTTON_COLOR;
  if (hasCustomPrimary) {
    rootDeclarations.push(`--bs-primary: ${primary};`);
    const triplet = hexToRgbTriplet(primary);
    if (triplet) {
      rootDeclarations.push(`--bs-primary-rgb: ${triplet};`);
    }
  }

  if (rootDeclarations.length === 0) {
    return '';
  }

  // Point body at the custom properties (rather than the raw values) so the
  // whole page and any `var(--course-*)` consumer resolve to one source.
  const bodyDeclarations: string[] = [];
  if (theme.bodyBackgroundColor) {
    bodyDeclarations.push('background-color: var(--course-bg);');
  }
  if (theme.bodyTextColor) {
    bodyDeclarations.push('color: var(--course-fg);');
  }

  const bodyRule =
    bodyDeclarations.length > 0
      ? `\nbody {\n  ${bodyDeclarations.join('\n  ')}\n}`
      : '';

  const buttonRules = hasCustomPrimary
    ? `
.btn-primary {
  --bs-btn-bg: ${primary};
  --bs-btn-border-color: ${primary};
  --bs-btn-hover-bg: color-mix(in srgb, ${primary} 85%, black);
  --bs-btn-hover-border-color: color-mix(in srgb, ${primary} 85%, black);
  --bs-btn-active-bg: color-mix(in srgb, ${primary} 75%, black);
  --bs-btn-active-border-color: color-mix(in srgb, ${primary} 75%, black);
  --bs-btn-disabled-bg: ${primary};
  --bs-btn-disabled-border-color: ${primary};
}
.btn-outline-primary {
  --bs-btn-color: ${primary};
  --bs-btn-border-color: ${primary};
  --bs-btn-hover-bg: ${primary};
  --bs-btn-hover-border-color: ${primary};
  --bs-btn-active-bg: ${primary};
  --bs-btn-active-border-color: ${primary};
  --bs-btn-disabled-color: ${primary};
  --bs-btn-disabled-border-color: ${primary};
}`
    : '';

  // Bootstrap's info alert is a fixed cyan that clashes with a themed accent.
  const alertRules = theme.accentColor
    ? `
.alert-info {
  --bs-alert-color: var(--course-surface-fg, var(--bs-body-color));
  --bs-alert-bg: var(--course-accent-faint);
  --bs-alert-border-color: var(--course-accent-soft);
  --bs-alert-link-color: var(--course-accent);
}`
    : '';

  return `:root {\n  ${rootDeclarations.join('\n  ')}\n}${bodyRule}${buttonRules}${alertRules}`;
}
